import { useRef, useState } from "react";
import { api, mediaUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Loader2, Trash2, Upload, Link2 } from "lucide-react";
import { useClubLogos, setClubLogos, opponentsOfPoster, findLogo } from "@/lib/clubLogos";

const AddForm = ({ name, setName, logos }) => {
  const fileRef = useRef(null);
  const [src, setSrc] = useState("");
  const [busy, setBusy] = useState(false);

  const save = async (payload) => {
    if (!name.trim()) return toast.error("Indiquez le nom du club");
    setBusy(true);
    try {
      const { data } = await api.post("/club-logos", { name, ...payload });
      setClubLogos([...logos.filter((l) => l.name !== data.name), data].sort((a, b) => a.name.localeCompare(b.name)));
      toast.success(`Logo ajouté : ${data.name}`);
      setName(""); setSrc("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec de l'ajout du logo");
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const onFile = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/upload", form, { headers: { "Content-Type": "multipart/form-data" } });
      await save({ url: data.url });
    } catch {
      toast.error("Échec de l'envoi de l'image");
      setBusy(false);
    }
  };

  return (
    <div className="space-y-2 rounded-lg border border-border p-3">
      <Label className="text-xs">Nom du club (tel qu'écrit sur l'affiche)</Label>
      <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="ex. R.SP.B. FRAMERIES" data-testid="club-logo-name-input" />
      <div className="flex gap-2">
        <Input value={src} onChange={(e) => setSrc(e.target.value)} placeholder="Adresse de l'image (clic droit → Copier l'adresse de l'image)"
          data-testid="club-logo-url-input" />
        <Button onClick={() => save({ source_url: src })} disabled={busy || !src} data-testid="club-logo-url-save">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Link2 className="h-4 w-4" />}
        </Button>
      </div>
      <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => onFile(e.target.files?.[0])} data-testid="club-logo-file-input" />
      <Button variant="secondary" size="sm" className="w-full" onClick={() => fileRef.current?.click()} disabled={busy} data-testid="club-logo-upload-button">
        <Upload className="mr-1 h-4 w-4" /> Ou envoyer une image depuis l'appareil
      </Button>
    </div>
  );
};

export const ClubLogosDialog = ({ open, onOpenChange, data }) => {
  const logos = useClubLogos();
  const [name, setName] = useState("");
  const missing = opponentsOfPoster(data).filter((n) => !findLogo(logos, n));

  const remove = async (id) => {
    try { await api.delete(`/club-logos/${id}`); setClubLogos(logos.filter((l) => l.id !== id)); }
    catch { toast.error("Échec de la suppression"); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl" data-testid="club-logos-dialog" aria-describedby={undefined}>
        <DialogHeader><DialogTitle className="font-display">Logos des clubs</DialogTitle></DialogHeader>
        <p className="text-xs text-muted-foreground">
          Ajoutez une fois le logo de chaque club, y compris le vôtre (nom : USC) : il s'affiche automatiquement devant son nom sur toutes les affiches.
        </p>
        {missing.length > 0 && (
          <div className="space-y-1.5">
            <Label className="text-xs">Clubs de cette affiche sans logo (cliquez pour l'ajouter)</Label>
            <div className="flex flex-wrap gap-1.5" data-testid="missing-logos">
              {missing.map((n) => (
                <button key={n} onClick={() => setName(n)}
                  className="rounded-full border border-border px-2.5 py-0.5 text-[11px] transition-colors hover:border-primary hover:text-primary">
                  {n}
                </button>
              ))}
            </div>
          </div>
        )}
        <AddForm name={name} setName={setName} logos={logos} />
        <div className="max-h-[30vh] space-y-1.5 overflow-y-auto" data-testid="club-logos-list">
          {logos.length === 0 ? (
            <p className="py-3 text-center text-sm text-muted-foreground">Aucun logo enregistré.</p>
          ) : logos.map((l) => (
            <div key={l.id} className="flex items-center gap-3 rounded-lg border border-border p-1.5" data-testid={`club-logo-${l.id}`}>
              <img src={mediaUrl(l.url)} alt="" className="h-8 w-8 rounded bg-white/90 object-contain p-0.5" />
              <span className="flex-1 truncate text-sm">{l.name}</span>
              <Button size="icon" variant="ghost" className="text-destructive hover:text-destructive" onClick={() => remove(l.id)}
                data-testid={`delete-club-logo-${l.id}`}>
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
};
